import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Music as MusicIcon, Mic } from "lucide-react";
import { Overline } from "./Motifs";
import { MUSIC } from "../data/content";

export default function Music() {
  return (
    <section
      id="music"
      data-testid="music-section"
      className="relative py-24 sm:py-32 bg-[#1E2A4F] text-[#F9F6F0] overflow-hidden"
    >
      {/* subtle terracotta circle */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#D95D39]/15 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Overline className="!text-[#E76F51]">Music & Voice</Overline>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95] tracking-tight">
              Songs, harmonies,
              <br />
              <em className="italic font-light">and a flash of jam.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#F9F6F0]/70 leading-relaxed">
            I sing the way I write — looking for the moment a small thing becomes a shared
            thing. A pop-up jam in a metro station. A late-night studio take.
          </div>
        </div>

        {/* Podcast feature */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch"
        >
          <div className="lg:col-span-7 rounded-lg overflow-hidden border border-[#F9F6F0]/15 bg-[#F9F6F0]/5 p-8 sm:p-12">
            <Overline className="!text-[#E76F51]">On Spotify</Overline>
            <h3 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
              {MUSIC.songs.title}
            </h3>
            <p className="text-[#F9F6F0]/70 mt-4 leading-relaxed max-w-md">
              {MUSIC.songs.subtitle}
            </p>

            <div className="mt-8 rounded-md overflow-hidden border border-[#F9F6F0]/10">
              <iframe
                title="Spotify - Divya Ramachandran songs"
                src={MUSIC.songs.embed}
                width="100%"
                height="232"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                data-testid="spotify-embed"
              />
            </div>

            <a
              href={MUSIC.songs.spotify}
              target="_blank"
              rel="noreferrer"
              data-testid="music-spotify-link"
              className="mt-6 inline-flex items-center gap-2 text-sm ink-link"
            >
              Open in Spotify <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="lg:col-span-5 grid gap-6">
            {MUSIC.flashJamImage && (
              <div
                className="rounded-lg overflow-hidden border border-[#F9F6F0]/15"
                data-testid="flash-jam-image"
              >
                <img
                  src={MUSIC.flashJamImage}
                  alt="Flash Jam — strangers walk in, a choir walks out"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            )}
            {MUSIC.projects.map((p, i) => (
              <div
                key={p.name}
                data-testid={`music-project-${i}`}
                className="p-6 sm:p-7 rounded-lg border border-[#F9F6F0]/15 bg-[#F9F6F0]/5 hover:bg-[#F9F6F0]/10 transition-colors duration-500"
              >
                <div className="flex items-center gap-3">
                  {i === 2 ? (
                    <Mic size={18} className="text-[#E76F51]" />
                  ) : (
                    <MusicIcon size={18} className="text-[#E76F51]" />
                  )}
                  <Overline className="!text-[#F9F6F0]/60">{p.role}</Overline>
                </div>
                <h4 className="font-display text-2xl sm:text-3xl mt-3">{p.name}</h4>
                <p className="text-sm text-[#F9F6F0]/70 mt-2 leading-relaxed">{p.blurb}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
